import type { Track } from '@/world/Track';
import type { ChaseTarget } from '@/camera/ChaseCamera';
import { tuning } from '@/config/tuning';
import { clamp, lerp, shortestAngleDelta } from '@/math/angles';
import { forwardX, forwardZ, rightX, rightZ } from '@/math/orientation';
import {
  arcadeCarStep,
  createCarState,
  type CarInput,
  type CarPhysicsState,
} from '@/physics/arcadeCar';
import { resolvePlaneWall, resolveWall } from '@/physics/collisions';
import { carReach, catchInNet, nearGoal, netDepthAt, netGripKeep, toGoalCoords } from '@/physics/goalNet';

const CAR_HALF_WIDTH = 1.0;
const CAR_HALF_LENGTH = 1.9; // nose/tail reach against the course end walls (M42)
/** Where a car meets the goal's slanted back net (M157): bumper height. */
const CAR_NET_H = 0.7;
const STOCK_NET_BOX = { halfLength: CAR_HALF_LENGTH, halfWidth: CAR_HALF_WIDTH };

/** Everything CarView/HUD need to draw a frame, interpolated for smoothness. */
export interface CarRenderSnapshot extends ChaseTarget {
  steerAngle: number;
  lateralSpeed: number;
}

/**
 * Car.ts — a car ENTITY: it owns the pure physics state and layers the track
 * interaction (continuous ground height + lateral wall clamp with a small
 * scrape restitution) on top of the pure step. Kept separate from CarView so
 * the simulation stays pure and testable.
 */
export class Car {
  readonly state: CarPhysicsState;
  y = 0;
  /** Vertical velocity + airborne flag — only non-trivial during a ramp launch. */
  vy = 0;
  airborne = false;
  hint = 0;
  /** Was the car pressed against a guardrail last step? (Wall-escape assist.) */
  private wallPinned = false;

  // Render-only visual offset (online netcode): when the server corrects this
  // car's predicted position, the correction is folded in HERE and decayed to
  // zero over a few frames, so the rendered car (and the camera that follows it)
  // ease to the truth instead of snapping. Always 0 in offline play.
  rOffX = 0;
  rOffY = 0;
  rOffZ = 0;
  rOffH = 0;

  // Previous step's pose, for render interpolation.
  private prevX = 0;
  private prevY = 0;
  private prevZ = 0;
  private prevHeading = 0;

  constructor(x: number, z: number, heading: number, y: number) {
    this.state = createCarState(x, z, heading);
    this.y = y;
    this.syncPrev();
  }

  reset(x: number, z: number, heading: number, y: number): void {
    this.state.x = x;
    this.state.z = z;
    this.state.heading = heading;
    this.state.vx = 0;
    this.state.vz = 0;
    this.state.steerAngle = 0;
    this.y = y;
    this.vy = 0;
    this.airborne = false;
    this.hint = 0;
    this.wallPinned = false;
    this.rOffX = this.rOffY = this.rOffZ = this.rOffH = 0;
    this.syncPrev();
  }

  private syncPrev(): void {
    this.prevX = this.state.x;
    this.prevY = this.y;
    this.prevZ = this.state.z;
    this.prevHeading = this.state.heading;
  }

  /** The pose was just written from OUTSIDE the step loop (a world seeded from
   *  a snapshot — M143): make the render's "previous" pose the current one and
   *  drop any pending correction glide, so the next frames draw the car where
   *  it IS instead of blending in from wherever it was built. */
  settleRenderPose(): void {
    this.rOffX = this.rOffY = this.rOffZ = this.rOffH = 0;
    this.syncPrev();
  }

  get speed(): number {
    return Math.hypot(this.state.vx, this.state.vz);
  }

  /** Signed sideways speed (for the visual body roll). */
  get lateralSpeed(): number {
    return this.state.vx * rightX(this.state.heading) + this.state.vz * rightZ(this.state.heading);
  }

  /** `params` lets the race hand AI rivals their (level-scaled) engine — the
   *  player always drives the plain `tuning.car`. */
  update(
    dt: number,
    input: CarInput,
    track: Track,
    params = tuning.car,
    /** The ride's real body (M153 tuning.vehicles[id].box) — what the finish
     *  net meets (M157). A stock box when the caller doesn't know the ride. */
    box: { halfLength: number; halfWidth: number } = STOCK_NET_BOX,
  ): void {
    this.syncPrev();

    // Wall-escape assist (M14): while pinned on a guardrail and backing up,
    // temper the steering so it can't re-jam the tail into the wall. (The yaw
    // ease + outward nudge are applied after the wall check below.)
    const p = params;
    // M40: the human gets the wall-bump + forward-escape; the AI keeps its M14/M17
    // wall behaviour (its recovery maneuvers are tuned around it). M49: the human
    // may drive per-vehicle params now, so the flag (not reference identity) says so.
    const isHuman = params.wallAssist === true;
    const vfPre =
      this.state.vx * forwardX(this.state.heading) + this.state.vz * forwardZ(this.state.heading);
    const escaping = this.wallPinned && input.throttle < -0.05 && vfPre < 1;
    const stepInput = escaping ? { ...input, steer: input.steer * p.wallReverseSteerScale } : input;

    // Free-space physics.
    arcadeCarStep(this.state, stepInput, params, dt);

    // Track interaction: continuous ground height + wall clamp.
    const proj = track.project(this.state.x, this.state.z, this.hint);
    this.hint = proj.index;

    if (escaping) {
      // Ease the nose back toward the road and break the wall's hold.
      const toTrack = shortestAngleDelta(this.state.heading, proj.dir);
      this.state.heading += clamp(toTrack, -1, 1) * p.wallReverseYaw * dt;
      const outSign = -Math.sign(proj.lateral) || 1; // away from this wall
      this.state.vx += proj.rightX * outSign * p.wallReverseKick * dt;
      this.state.vz += proj.rightZ * outSign * p.wallReverseKick * dt;
    } else if (isHuman && this.wallPinned) {
      // M40 forward-escape: once the player has aimed the nose OFF the rail, give a
      // small outward "peel" so they drive straight out FORWARDS (no reverse needed).
      // While the nose still points INTO the wall we DON'T help — they steer out first
      // (so deliberately hugging/cutting a rail, and the M14 reverse test, are untouched).
      const fDotR =
        forwardX(this.state.heading) * proj.rightX + forwardZ(this.state.heading) * proj.rightZ;
      const noseOutward = -Math.sign(proj.lateral) * fDotR;
      if (noseOutward > 0.05) {
        const outSign = -Math.sign(proj.lateral) || 1;
        this.state.vx += proj.rightX * outSign * p.wallForwardKick * dt;
        this.state.vz += proj.rightZ * outSign * p.wallForwardKick * dt;
      }
    }

    // Vertical: glued to the ground normally; once a launch ramp sets `airborne`
    // (+ vy), follow a ballistic arc until it lands again (§M8 Times Square stairs).
    const groundY = proj.y;
    if (this.airborne) {
      this.vy -= tuning.car.gravity * dt;
      this.y += this.vy * dt;
      if (this.y <= groundY) {
        this.y = groundY;
        this.vy = 0;
        this.airborne = false;
      }
    } else {
      this.y = groundY;
    }

    // Lateral wall clamp + scrape (shared, tested helper). M40: the HUMAN clamps a
    // hair inside the rail (wallMargin → never looks embedded) and bumps off livelier
    // (wallBumpRestitution); the AI keeps the stock limit + scrape it's tuned around.
    const wallLimit = track.halfWidth - CAR_HALF_WIDTH - (isHuman ? p.wallMargin : 0);
    const w = resolveWall(
      proj.lateral,
      wallLimit,
      proj.rightX,
      proj.rightZ,
      this.state.vx,
      this.state.vz,
      isHuman ? p.wallBumpRestitution : p.wallRestitution,
    );
    if (w) {
      this.state.x += w.pushX;
      this.state.z += w.pushZ;
      this.state.vx = w.vx;
      this.state.vz = w.vz;
    }
    // "Pinned" = at (or a whisker off) the clamp limit — the escape assist
    // keeps helping until the car is genuinely clear of the rail.
    this.wallPinned = Math.abs(proj.lateral) > wallLimit - 0.4;

    // Course end walls (M42): stop reversing off the back of the grid and
    // close the finish run-out. No-ops anywhere mid-course. Shortcut channels
    // opt out (M117c) — their entry stays open for the deck-edge back-out.
    if (track.noEndWalls) return;
    for (const wall of [track.endWalls.back, track.endWalls.end]) {
      const ew = resolvePlaneWall(
        this.state.x,
        this.state.z,
        CAR_HALF_LENGTH,
        wall.x,
        wall.z,
        wall.nx,
        wall.nz,
        this.state.vx,
        this.state.vz,
        isHuman ? p.wallBumpRestitution : p.wallRestitution,
      );
      if (ew) {
        this.state.x += ew.pushX;
        this.state.z += ew.pushZ;
        this.state.vx = ew.vx;
        this.state.vz = ew.vz;
      }
    }

    // THE FINISH GOAL (M157): the back net catches a car the way it catches a
    // ball — a finished car coasting through the line ends up resting in the
    // goal instead of rolling on down the run-out. Same pure physics as the
    // ball (physics/goalNet.ts); the car's reach is its OWN body seen from
    // the net (a long-nosed ride on the stock box poked through the mesh —
    // measured in game, the reviewer's catch).
    const g = track.goalFrame;
    if (!g) return;
    const c = toGoalCoords(g, this.state.x, this.state.z);
    const cosA = forwardX(this.state.heading) * g.fx + forwardZ(this.state.heading) * g.fz;
    const reach = carReach(cosA, box.halfLength, box.halfWidth);
    if (!nearGoal(g, c, reach)) return;
    const vs = this.state.vx * g.fx + this.state.vz * g.fz;
    const net = catchInNet(c.s, reach, netDepthAt(this.y - g.y + CAR_NET_H), vs, dt);
    if (!net) return;
    const vl = (this.state.vx * g.rx + this.state.vz * g.rz) * netGripKeep(dt);
    this.state.x += g.fx * (net.s - c.s);
    this.state.z += g.fz * (net.s - c.s);
    this.state.vx = g.fx * net.vAlong + g.rx * vl;
    this.state.vz = g.fz * net.vAlong + g.rz * vl;
  }

  snapshot(alpha: number): CarRenderSnapshot {
    return {
      x: lerp(this.prevX, this.state.x, alpha) + this.rOffX,
      y: lerp(this.prevY, this.y, alpha) + this.rOffY,
      z: lerp(this.prevZ, this.state.z, alpha) + this.rOffZ,
      heading:
        this.prevHeading + shortestAngleDelta(this.prevHeading, this.state.heading) * alpha + this.rOffH,
      speed: this.speed,
      steerAngle: this.state.steerAngle,
      lateralSpeed: this.lateralSpeed,
    };
  }
}
